import React, { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Upload, ExternalLink, Loader2, Trash2, CalendarDays, Check, X } from 'lucide-react';
import { api, openEquipmentDocDownload } from '../../api/client';
import { formatCalendarDate, parseCalendarDate, toCalendarDateString } from '../../lib/calendar-date';
import type { EquipmentDocument } from '../../types/equipment';
import { Tooltip } from '../ui/tooltip';

const STATUS_BADGE: Record<string, string> = {
  current:   'bg-green-500/20 text-green-400 border border-green-500/30',
  expiring:  'bg-amber-500/20 text-amber-400 border border-amber-500/30',
  expired:   'bg-red-500/20 text-red-400 border border-red-500/30',
  no_expiry: 'bg-fieldiq-dark text-fieldiq-muted border border-fieldiq-border',
};

const STATUS_LABEL: Record<string, string> = {
  current:   'Current',
  expiring:  'Expiring Soon',
  expired:   'Expired',
  no_expiry: 'No Expiry',
};

const DOC_TYPES = [
  { value: 'registration', label: 'Registration' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'permit', label: 'Permit' },
  { value: 'other', label: 'Other' },
];

interface AssetDocumentsProps {
  equipmentId: string;
}

export function AssetDocuments({ equipmentId }: AssetDocumentsProps) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [pendingDocType, setPendingDocType] = useState('other');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [pendingExpiry, setPendingExpiry] = useState('');
  const [editingExpiryId, setEditingExpiryId] = useState<string | null>(null);
  const [editExpiryValue, setEditExpiryValue] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['equipment-documents', equipmentId],
    queryFn: () => api.equipment.getDocuments(equipmentId).then(r => {
      const d = r.data?.data ?? r.data ?? [];
      return (Array.isArray(d) ? d : []) as EquipmentDocument[];
    }),
    enabled: !!equipmentId,
  });
  const documents: EquipmentDocument[] = data ?? [];

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_type', pendingDocType);
      formData.append('doc_label', file.name.replace(/\.[^.]+$/, ''));
      if (pendingExpiry) formData.append('expiry_date', pendingExpiry);
      await api.equipment.createDocument(equipmentId, formData);
      queryClient.invalidateQueries({ queryKey: ['equipment-documents', equipmentId] });
      setPendingExpiry('');
      toast.success('Document uploaded');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const deleteMutation = useMutation({
    mutationFn: (docId: string) => api.equipment.deleteDocument(docId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment-documents', equipmentId] });
      setDeleteConfirmId(null);
      toast.success('Document removed');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to remove document');
    },
  });

  const updateExpiryMutation = useMutation({
    mutationFn: ({ docId, expiry_date }: { docId: string; expiry_date: string | null }) =>
      api.equipment.updateDocument(docId, { expiry_date }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment-documents', equipmentId] });
      setEditingExpiryId(null);
      toast.success('Expiry date updated');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to update expiry');
    },
  });

  return (
    <div className="bg-fieldiq-card rounded-lg border border-fieldiq-border overflow-hidden">
      <div className="p-4 border-b border-fieldiq-border flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-bold text-white">Documents</h3>
          <p className="text-xs text-fieldiq-text mt-0.5">
            {documents.length} document{documents.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={pendingDocType}
            onChange={e => setPendingDocType(e.target.value)}
            className="bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2.5 py-1.5 text-white text-xs focus:outline-none focus:border-amber-500/50"
          >
            {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <input
            type="date"
            value={pendingExpiry}
            onChange={e => setPendingExpiry(e.target.value)}
            placeholder="Expiry date"
            className="bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2.5 py-1.5 text-white text-xs focus:outline-none focus:border-amber-500/50 w-[130px]"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1.5 bg-fieldiq-accent text-fieldiq-dark text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-amber-400 transition-colors disabled:opacity-50"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? 'Uploading…' : 'Upload doc'}
          </button>
        </div>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.heic"
        className="hidden"
        onChange={e => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) void handleUpload(f);
        }}
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-5 h-5 text-fieldiq-accent animate-spin" />
        </div>
      ) : documents.length === 0 ? (
        <div className="py-8 text-center text-fieldiq-muted text-sm">
          No documents yet. Click "Upload doc" to add registration, insurance, or inspection paperwork.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-fieldiq-dark text-fieldiq-muted text-xs uppercase">
                <th className="text-left px-4 py-2.5 font-medium">Document</th>
                <th className="text-left px-4 py-2.5 font-medium">Type</th>
                <th className="text-left px-4 py-2.5 font-medium">Expiry</th>
                <th className="text-left px-4 py-2.5 font-medium">Status</th>
                <th className="text-right px-4 py-2.5 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {documents.map(doc => (
                <tr key={doc.id} className="border-t border-fieldiq-border hover:bg-fieldiq-dark/50 transition-colors">
                  <td className="px-4 py-3 text-white">{doc.doc_label || doc.doc_type}</td>
                  <td className="px-4 py-3 text-fieldiq-text capitalize whitespace-nowrap">
                    {(doc.doc_type || '').replace(/_/g, ' ') || '—'}
                  </td>
                  <td className="px-4 py-3 text-fieldiq-text whitespace-nowrap">
                    {editingExpiryId === doc.id ? (
                      <div className="flex items-center gap-1.5">
                        <input
                          type="date"
                          value={editExpiryValue}
                          onChange={e => setEditExpiryValue(e.target.value)}
                          className="bg-fieldiq-dark border border-fieldiq-border rounded px-1.5 py-0.5 text-white text-xs focus:outline-none focus:border-amber-500/50 w-[120px]"
                          autoFocus
                        />
                        <Tooltip content="Save">
                          <button
                            type="button"
                            onClick={() => updateExpiryMutation.mutate({
                              docId: doc.id,
                              expiry_date: editExpiryValue || null,
                            })}
                            disabled={updateExpiryMutation.isPending}
                            className="text-green-400 hover:text-green-300"
                            aria-label="Save"
                          >
                            <Check size={14} />
                          </button>
                        </Tooltip>
                        <Tooltip content="Cancel">
                          <button
                            type="button"
                            onClick={() => setEditingExpiryId(null)}
                            className="text-fieldiq-muted hover:text-white"
                            aria-label="Cancel"
                          >
                            <X size={14} />
                          </button>
                        </Tooltip>
                      </div>
                    ) : (
                      <Tooltip content="Edit expiry date">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingExpiryId(doc.id);
                            const parsed = parseCalendarDate(doc.expiry_date);
                            setEditExpiryValue(parsed ? toCalendarDateString(parsed) : '');
                          }}
                          className="flex items-center gap-1 hover:text-fieldiq-accent transition-colors"
                        >
                          {formatCalendarDate(doc.expiry_date, {}, 'en-CA') ?? '—'}
                          <CalendarDays size={12} className="text-fieldiq-muted" />
                        </button>
                      </Tooltip>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[doc.status] || STATUS_BADGE.no_expiry}`}>
                      {STATUS_LABEL[doc.status] || 'Unknown'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-3">
                      {doc.file_url ? (
                        <button
                          type="button"
                          onClick={() => {
                            openEquipmentDocDownload(doc.id).catch(() =>
                              toast.error('Failed to open document')
                            );
                          }}
                          className="inline-flex items-center gap-1 text-xs text-fieldiq-accent hover:text-amber-300 transition-colors"
                        >
                          View <ExternalLink size={12} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => fileRef.current?.click()}
                          className="text-xs text-fieldiq-muted hover:text-fieldiq-accent transition-colors"
                        >
                          Upload doc
                        </button>
                      )}
                      <Tooltip content="Remove document">
                        <button
                          type="button"
                          onClick={() => setDeleteConfirmId(doc.id)}
                          className="text-fieldiq-muted hover:text-red-400 transition-colors"
                          aria-label="Remove document"
                        >
                          <Trash2 size={14} />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-fieldiq-card rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="text-lg font-bold text-white mb-2">Remove Document</h3>
            <p className="text-sm text-fieldiq-text mb-6">
              Remove this document from the asset? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                disabled={deleteMutation.isPending}
                className="flex-1 py-2.5 bg-fieldiq-dark text-white font-medium rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirmId)}
                disabled={deleteMutation.isPending}
                className="flex-1 py-2.5 bg-red-500 text-white font-semibold rounded-lg text-sm hover:bg-red-400 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AssetDocuments;
